import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    AppBar,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
    Typography
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import glossaryServiceInstance from '../../services/GlossaryService';
import textFileServiceInstance from '../../services/TextFileService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { LANGUAGE_MAP } from '../../utils/LanguageNames';
import { isAdmin, isGlossaryManager } from '../../utils/authUtils';
import ModernBreadcrumbs from '../navigation/ModernBreadcrumbs';

const ALLOWED_SOURCE_STAGES = new Set([
    'LEVEL2_WIP',
    'LEVEL2_SEVAK_COMPLETE',
    'LEVEL3_WIP',
    'LEVEL3_SEVAK_COMPLETE',
    'PUBLISHED',
]);

const LANGUAGE_TO_CODE = Object.fromEntries(Object.entries(LANGUAGE_MAP).map(([code, name]) => [name, code]));

function glossaryKey(filename, subfolder) {
    return `${filename}::${subfolder || ''}`;
}

function normalizeGlossaryList(payload) {
    const data = typeof payload === 'string' ? (() => { try { return JSON.parse(payload); } catch { return payload; } })() : payload;
    if (!data) return [];
    if (Array.isArray(data)) {
        return data.flatMap((item) => {
            if (typeof item === 'string') return [{ filename: item, subfolder: '' }];
            if (item && typeof item === 'object') {
                const fn = item.filename || item.name || item.file || '';
                return fn ? [{ filename: fn, subfolder: item.subfolder || item.folder || '' }] : [];
            }
            return [];
        });
    }
    if (typeof data === 'object') {
        const list = data.glossaries || data.files || data.data || data.items || data.results;
        if (Array.isArray(list)) return normalizeGlossaryList(list);
    }
    return [];
}

function parseFilename(filename) {
    // "guj_eng.json" → { sourceCode: 'guj', targetCode: 'eng' }
    if (!filename) return null;
    const base = filename.replace(/\.json$/i, '');
    const idx = base.indexOf('_');
    if (idx < 0) return null;
    return { sourceCode: base.substring(0, idx), targetCode: base.substring(idx + 1) };
}

function parseJsonIfString(value) {
    if (typeof value !== 'string') return value;
    try { return JSON.parse(value); } catch { return value; }
}

function normalizeTerms(anyPayload) {
    const payload = parseJsonIfString(anyPayload);
    if (!payload) return {};
    if (typeof payload === 'object' && !Array.isArray(payload)) {
        if (payload.terms) return normalizeTerms(payload.terms);
        if (payload.glossary) return normalizeTerms(payload.glossary);
        const entries = Object.entries(payload).filter(([, value]) => typeof value === 'string');
        if (entries.length > 0) return Object.fromEntries(entries);
    }
    if (Array.isArray(payload)) {
        const entries = [];
        payload.forEach((item) => {
            if (!item || typeof item !== 'object') return;
            const source = item.source || item.key || item.term || item.source_term;
            const target = item.target || item.value || item.translation || item.target_term;
            if (source && target) entries.push([String(source), String(target)]);
        });
        return Object.fromEntries(entries);
    }
    return {};
}

export default function GlossaryEditorPage() {
    useTrackUsage('GlossaryEditorPage', 'editor', 'glossary');
    // videoId is optional — undefined when opened as a system-wide glossary manager
    // textFileId is set when opened from the text file editor
    const { videoId, textFileId } = useParams();
    const isTextFile = !!textFileId && !videoId;
    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = location.state?.fromPath || '/';
    const backTo = location.state?.backTo || null;

    const [videoData, setVideoData] = useState(null);
    const [textFileData, setTextFileData] = useState(null);
    const [loadingVideo, setLoadingVideo] = useState(false);
    const [sourceLanguage, setSourceLanguage] = useState('Gujarati');
    const [targetLanguage, setTargetLanguage] = useState('English');
    const [subfolder, setSubfolder] = useState('');

    const [systemGlossaries, setSystemGlossaries] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    // Unique subfolders from AI server list, empty string = root, sorted root-first
    const knownSubfolders = useMemo(() => {
        const seen = new Set();
        const result = [];
        for (const g of systemGlossaries) {
            const sf = g.subfolder || '';
            if (!seen.has(sf)) { seen.add(sf); result.push(sf); }
        }
        result.sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
        return result;
    }, [systemGlossaries]);
    // Display labels for Autocomplete ('' shown as '(root — default)')
    const subfolderOptions = useMemo(
        () => knownSubfolders.map((sf) => (sf === '' ? '(root — default)' : sf)),
        [knownSubfolders]
    );

    const [selectedGlossaryKey, setSelectedGlossaryKey] = useState('');

    const [existingTerms, setExistingTerms] = useState({});
    const [generatedTerms, setGeneratedTerms] = useState({});
    const [selectedRows, setSelectedRows] = useState({});
    const [editedTargets, setEditedTargets] = useState({});
    const [deletedSources, setDeletedSources] = useState(new Set());

    const [metadata, setMetadata] = useState(null);
    const [loadingTerms, setLoadingTerms] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [savedTermCount, setSavedTermCount] = useState(null);
    const [autoSelectDone, setAutoSelectDone] = useState(false);
    const [termFilter, setTermFilter] = useState('');
    const [editUnlocked, setEditUnlocked] = useState(new Set());
    const [showAllTerms, setShowAllTerms] = useState(false);

    // Pending (staged) changes — loaded for glossary managers / admins
    // pendingTerms: { [source]: PendingTerm[] } — each source word may have multiple proposals
    const [pendingTerms, setPendingTerms] = useState({});
    const [loadingPending, setLoadingPending] = useState(false);
    const [approving, setApproving] = useState(false);
    const [showPendingReview, setShowPendingReview] = useState(false);

    const canManage = isAdmin() || isGlossaryManager();

    const loadVersionRef = useRef(0);

    const breadcrumbPath = useMemo(() => {
        const fromState = fromPath && fromPath !== '/' ? fromPath : '';
        const fromTextFile = textFileData?.path || '';
        const raw = fromState || fromTextFile || '/';
        return raw.startsWith('/') ? raw : `/${raw}`;
    }, [fromPath, textFileData]);

    // On mount: load video (if videoId provided) + load system glossary list in parallel
    useEffect(() => {
        setError('');
        setSuccessMessage('');
        setSavedTermCount(null);
        setExistingTerms({});
        setGeneratedTerms({});
        setSelectedRows({});
        setEditedTargets({});
        setDeletedSources(new Set());
        setMetadata(null);
        setSelectedGlossaryKey('');
        setAutoSelectDone(false);
        setTermFilter('');
        loadVersionRef.current = 0;
        setEditUnlocked(new Set());
        setShowAllTerms(false);
        setVideoData(null);
        setTextFileData(null);

        if (isTextFile) {
            setLoadingVideo(true);
            textFileServiceInstance
                .getTextFileById(textFileId)
                .then((response) => {
                    setTextFileData(response);
                    const translations = response.translations || {};
                    const langs = Object.keys(translations);
                    const src = response.sourceLanguage || langs[0] || '';
                    const tgt = langs.find((l) => l !== src) || '';
                    setSourceLanguage(src);
                    setTargetLanguage(tgt);
                })
                .catch((fetchError) => {
                    console.error('Failed to load text file details for glossary editor', fetchError);
                    setError('Failed to load text file details.');
                })
                .finally(() => setLoadingVideo(false));
        } else if (videoId) {
            setLoadingVideo(true);
            videoCRUDServiceInstance
                .getVideoById(videoId)
                .then((response) => {
                    const data = response.data;
                    setVideoData(data);
                    const names = (data.videoLanguages || []).map((item) => item.languageName);
                    if (names.length > 0) {
                        // Prefer Gujarati as source, English as target if available
                        const hasGujarati = names.includes('Gujarati');
                        const hasEnglish = names.includes('English');
                        if (hasGujarati && hasEnglish) {
                            setSourceLanguage('Gujarati');
                            setTargetLanguage('English');
                        } else {
                            setSourceLanguage(names[0]);
                            setTargetLanguage(names.length > 1 ? names[1] : names[0]);
                        }
                    }
                })
                .catch((fetchError) => {
                    console.error('Failed to load video details for glossary editor', fetchError);
                    setError('Failed to load video details.');
                })
                .finally(() => setLoadingVideo(false));
        }

        setLoadingList(true);
        glossaryServiceInstance
            .listGlossaries()
            .then((data) => setSystemGlossaries(normalizeGlossaryList(data)))
            .catch((err) => console.error('Failed to load system glossaries', err))
            .finally(() => setLoadingList(false));
    }, [videoId, textFileId, isTextFile]);

    const languageStages = useMemo(() => {
        if (isTextFile) {
            // Get the processStage from each text file translation
            const map = {};
            const translations = textFileData?.translations || {};
            Object.entries(translations).forEach(([lang, langData]) => {
                map[lang] = langData?.processStage || 'LEVEL1_WIP';
            });
            return map;
        }
        const map = {};
        (videoData?.videoLanguages || []).forEach((item) => {
            map[item.languageName] = item.processStage;
        });
        return map;
    }, [videoData, textFileData, isTextFile]);

    const stageError = useMemo(() => {
        if (!targetLanguage) return '';
        const stage = languageStages[targetLanguage];
        if (stage === undefined) {
            return isTextFile
                ? `${targetLanguage} is not available in this text file.`
                : `${targetLanguage} is not available in this video.`;
        }
        if (!ALLOWED_SOURCE_STAGES.has(stage)) {
            return `${targetLanguage} is at ${stage}; Level 2+ is required to generate a glossary.`;
        }
        return '';
    }, [targetLanguage, languageStages, isTextFile]);

    const filename = useMemo(() => {
        if (!sourceLanguage || !targetLanguage) return '';
        const sourceCode = LANGUAGE_TO_CODE[sourceLanguage];
        const targetCode = LANGUAGE_TO_CODE[targetLanguage];
        if (!sourceCode || !targetCode) return '';
        return `${sourceCode}_${targetCode}.json`;
    }, [sourceLanguage, targetLanguage]);

    const handleLoadGlossary = useCallback(async (fn, sf) => {        if (!fn) return;
        const version = ++loadVersionRef.current;
        setLoadingTerms(true);
        try {
            const data = await glossaryServiceInstance.getGlossary(fn, sf || '');
            if (version !== loadVersionRef.current) return;
            setExistingTerms(normalizeTerms(data?.terms ?? data));
            setMetadata(data?.metadata || null);
        } catch (err) {
            if (version !== loadVersionRef.current) return;
            console.error('Failed to load glossary terms', err);
            setError(err?.response?.data?.error || 'Failed to load glossary terms.');
        } finally {
            if (version === loadVersionRef.current) setLoadingTerms(false);
        }
    }, []);

    // Auto-highlight matching glossary once video + list are both ready.
    // Also auto-LOAD if a matching glossary exists (language pair already known).
    useEffect(() => {
        if (autoSelectDone || !filename || systemGlossaries.length === 0) return;
        // Only require videoData if videoId is provided
        if (videoId && !videoData) return;
        setAutoSelectDone(true);
        // Always prefer the Guj-Eng 'test' glossary as the default, fall back to any filename match
        const match =
            systemGlossaries.find((g) => g.filename === filename && (g.subfolder || '') === 'test') ||
            systemGlossaries.find((g) => g.filename === filename);
        if (match) {
            const key = glossaryKey(match.filename, match.subfolder);
            setSelectedGlossaryKey(key);
            setSubfolder(match.subfolder || '');
            handleLoadGlossary(match.filename, match.subfolder);
        }
    }, [autoSelectDone, videoData, videoId, filename, systemGlossaries, handleLoadGlossary]);

    const handleSelectSystemGlossary = (glossary) => {
        const { filename: fn, subfolder: sf } = glossary;
        const key = glossaryKey(fn, sf);
        if (key === selectedGlossaryKey) return;
        setAutoSelectDone(true);
        setSelectedGlossaryKey(key);
        setSuccessMessage('');
        setSavedTermCount(null);
        setGeneratedTerms({});
        setEditedTargets({});
        setDeletedSources(new Set());
        setExistingTerms({});
        setTermFilter('');
        setEditUnlocked(new Set());
        setShowAllTerms(false);
        setError('');
        const parsed = parseFilename(fn);
        if (parsed) {
            const srcName = LANGUAGE_MAP[parsed.sourceCode];
            const tgtName = LANGUAGE_MAP[parsed.targetCode];
            if (srcName) setSourceLanguage(srcName);
            if (tgtName) setTargetLanguage(tgtName);
        }
        setSubfolder(sf || '');
        handleLoadGlossary(fn, sf);
        if (canManage) {
            setPendingTerms({});
            setLoadingPending(true);
            glossaryServiceInstance
                .getPending(fn, sf || '')
                .then((data) => setPendingTerms(data?.changes || {}))
                .catch((err) => console.error('Failed to load pending terms', err))
                .finally(() => setLoadingPending(false));
        }
    };

    const rows = useMemo(() => {
        const keys = new Set([...Object.keys(existingTerms), ...Object.keys(generatedTerms)]);
        return Array.from(keys)
            .sort((a, b) => a.localeCompare(b))
            .map((source) => {
                const existingTarget = existingTerms[source] || '';
                const generatedTarget = generatedTerms[source] || '';
                const editedTarget = editedTargets[source];
                const isMarkedDeleted = deletedSources.has(source);
                const finalTarget = isMarkedDeleted ? '' : (editedTarget ?? (generatedTarget || existingTarget));
                let status;
                if (isMarkedDeleted) {
                    status = 'delete';
                } else if (!generatedTarget && !editedTarget) {
                    status = 'existing-only';
                } else if (!existingTarget) {
                    status = 'new';
                } else if (existingTarget && (generatedTarget || editedTarget) &&
                           (editedTarget ?? generatedTarget) !== existingTarget) {
                    status = 'conflict';
                } else {
                    status = 'existing-only';
                }
                return { source, existingTarget, generatedTarget, finalTarget, status };
            });
    }, [existingTerms, generatedTerms, editedTargets, deletedSources]);

    const MAX_DISPLAY_ROWS = 200;
    const displayedRows = useMemo(() => {
        const source = showAllTerms ? rows : rows.filter((r) => r.status !== 'existing-only');
        if (!termFilter.trim()) return source.slice(0, MAX_DISPLAY_ROWS);
        const lc = termFilter.toLowerCase();
        return source
            .filter((r) => r.source.toLowerCase().includes(lc) || r.finalTarget.toLowerCase().includes(lc))
            .slice(0, MAX_DISPLAY_ROWS);
    }, [rows, termFilter, showAllTerms]);

    const existingTermCount = Object.keys(existingTerms).length;
    const generatedTermCount = Object.keys(generatedTerms).length;
    const pendingRows = useMemo(() => rows.filter((r) => r.status !== 'existing-only'), [rows]);
    const saveableCount = useMemo(
        () => rows.filter((row) => {
            const hasExisting = Object.prototype.hasOwnProperty.call(existingTerms, row.source);
            const isMarkedDeleted = deletedSources.has(row.source);
            const currentValue = String(row.finalTarget || '').trim();
            const existingValue = String(existingTerms[row.source] || '').trim();

            if (hasExisting) {
                if (isMarkedDeleted) return true;
                if (!selectedRows[row.source]) return false;
                return currentValue !== existingValue;
            }

            return Boolean(selectedRows[row.source]) && Boolean(currentValue);
        }).length,
        [rows, existingTerms, deletedSources, selectedRows]
    );

    useEffect(() => {
        setSelectedRows((prev) => {
            const next = {};
            rows.forEach((row) => {
                if (row.existingTarget) {
                    next[row.source] = true;
                    return;
                }
                next[row.source] = prev[row.source] ?? (row.status === 'new');
            });
            return next;
        });
    }, [rows]);

    const handleGenerate = async () => {
        if (stageError) { setError(stageError); return; }
        if (!sourceLanguage || !targetLanguage) { setError('Please select both source and target languages.'); return; }
        setError('');
        setSuccessMessage('');
        setGenerating(true);
        try {
            let data;
            if (isTextFile) {
                data = await textFileServiceInstance.generateGlossary(textFileId, sourceLanguage, targetLanguage);
            } else {
                data = await glossaryServiceInstance.generateGlossary(videoId, sourceLanguage, targetLanguage, subfolder);
            }
            setGeneratedTerms(normalizeTerms(data));
        } catch (generateError) {
            console.error('Failed to generate glossary', generateError);
            setError(generateError?.response?.data?.error || 'Failed to generate glossary.');
        } finally {
            setGenerating(false);
        }
    };

    const handleSave = async () => {
        if (!filename) { setError('Unable to derive glossary filename from selected languages.'); return; }
        const payload = {};

        // Start with existing terms so unchanged rows are preserved.
        Object.assign(payload, existingTerms);

        // Apply additions/updates/deletions from current table state.
        rows.forEach((row) => {
            const hasExisting = Object.prototype.hasOwnProperty.call(existingTerms, row.source);
            const hasExplicitEdit = Object.prototype.hasOwnProperty.call(editedTargets, row.source);
            const hasMarkedForDelete = deletedSources.has(row.source);
            const finalVal = String(row.finalTarget || '').trim();

            if (hasExisting) {
                if (hasMarkedForDelete) {
                    delete payload[row.source];
                    return;
                }

                // Clearing an existing term explicitly removes it from glossary.
                if (hasExplicitEdit && !finalVal) {
                    delete payload[row.source];
                    return;
                }

                // Existing terms are always selected, so keep or update them.
                if (selectedRows[row.source] && finalVal) {
                    payload[row.source] = finalVal;
                }
                return;
            }

            // New/generated-only terms: include only if selected and non-empty.
            if (!selectedRows[row.source]) return;
            if (!finalVal) return;
            payload[row.source] = finalVal;
        });

        if (Object.keys(payload).length === 0 && rows.length === 0) { setError('No terms to save.'); return; }
        setSaving(true);
        setError('');
        setSuccessMessage('');
        setSavedTermCount(null);
        try {
            const saveResponse = await glossaryServiceInstance.saveGlossary(
                filename,
                subfolder,
                payload,
                metadata?.version ?? null,
            );
            const rawTermCount = saveResponse?.term_count ?? saveResponse?.serverResponse?.term_count;
            const parsedTermCount = Number.isFinite(Number(rawTermCount)) ? Number(rawTermCount) : null;
            setSuccessMessage(saveResponse?.message || saveResponse?.serverResponse?.message || 'Glossary saved successfully.');
            setSavedTermCount(parsedTermCount);
            glossaryServiceInstance
                .listGlossaries()
                .then((data) => setSystemGlossaries(normalizeGlossaryList(data)))
                .catch((err) => console.error('Failed to refresh system glossaries', err));
            setSelectedGlossaryKey(glossaryKey(filename, subfolder));
            await handleLoadGlossary(filename, subfolder);
        } catch (saveError) {
            console.error('Failed to save glossary', saveError);
            if (saveError?.response?.status === 409) {
                setError(saveError?.response?.data?.error || 'Glossary was updated by someone else. Please reload and try again.');
                await handleLoadGlossary(filename, subfolder);
            } else {
                setError(saveError?.response?.data?.error || 'Failed to save glossary.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleSubmitPending = async () => {
        if (!filename) { setError('Unable to derive glossary filename from selected languages.'); return; }
        const changes = {};
        rows.forEach((row) => {
            const hasExisting = Object.prototype.hasOwnProperty.call(existingTerms, row.source);
            const hasExplicitEdit = Object.prototype.hasOwnProperty.call(editedTargets, row.source);
            const isMarkedForDelete = deletedSources.has(row.source);
            const finalVal = String(row.finalTarget || '').trim();

            if (isMarkedForDelete && hasExisting) {
                changes[row.source] = { operation: 'delete', target: null, previousTarget: row.existingTarget };
            } else if (hasExisting && hasExplicitEdit && finalVal && finalVal !== row.existingTarget) {
                changes[row.source] = { operation: 'update', target: finalVal, previousTarget: row.existingTarget };
            } else if (!hasExisting && selectedRows[row.source] && finalVal) {
                changes[row.source] = { operation: 'add', target: finalVal, previousTarget: null };
            }
        });

        if (Object.keys(changes).length === 0) { setError('No changes to submit for review.'); return; }
        setSaving(true);
        setError('');
        setSuccessMessage('');
        try {
            await glossaryServiceInstance.submitPending(filename, subfolder, changes);
            setSuccessMessage(`${Object.keys(changes).length} change(s) submitted for review.`);
            setEditedTargets({});
            setDeletedSources(new Set());
            setEditUnlocked(new Set());
            // Reload pending panel for managers viewing the same glossary
            if (canManage) {
                glossaryServiceInstance
                    .getPending(filename, subfolder)
                    .then((data) => setPendingTerms(data?.changes || {}))
                    .catch((err) => console.error('Failed to reload pending', err));
            }
        } catch (err) {
            setError(err?.response?.data?.error || 'Failed to submit changes for review.');
        } finally {
            setSaving(false);
        }
    };

    // approved: { [source]: proposalIndex } — pick which proposal to apply per word
    // rejected: string[] — source words whose all proposals are discarded
    const handleApproveTerms = async (approved, rejected) => {
        if (!filename) return;
        setApproving(true);
        setError('');
        try {
            const result = await glossaryServiceInstance.approvePending(filename, subfolder, approved, rejected);
            setSuccessMessage(result?.message || 'Changes applied.');
            // Reload live terms and pending queue
            await handleLoadGlossary(filename, subfolder);
            const pending = await glossaryServiceInstance.getPending(filename, subfolder);
            const updatedChanges = pending?.changes || {};
            setPendingTerms(updatedChanges);
            // Auto-close review panel when all items have been processed
            if (Object.keys(updatedChanges).length === 0) {
                setShowPendingReview(false);
            }
        } catch (err) {
            setError(err?.response?.data?.error || 'Failed to apply changes.');
        } finally {
            setApproving(false);
        }
    };

    const videoTitle = isTextFile
        ? (textFileData?.name || textFileData?.title || textFileData?.fileName || null)
        : (videoData?.videoName || videoData?.title || videoData?.fileName || null);

    const handleBreadcrumbNavigate = (crumb) => {
        navigate('/', { state: { navigateToFolder: crumb.path } });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            {/* ── Top AppBar with breadcrumbs ── */}
            <AppBar position="static" color="default" elevation={1}>
                <Toolbar variant="dense" sx={{ gap: 1, minHeight: 56 }}>
                    <ModernBreadcrumbs
                        currentPath={breadcrumbPath}
                        onNavigate={handleBreadcrumbNavigate}
                        isDarkBackground={true}
                        maxItems={6}
                    />
                    <Typography variant="h6" sx={{ flexShrink: 0, color: '#fff', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        Glossary Editor
                        {videoTitle ? (
                            <Typography component="span" variant="body2" sx={{ ml: 1, color: 'rgba(255,255,255,0.8)' }}>
                                — {videoTitle}
                            </Typography>
                        ) : null}
                    </Typography>
                    {loadingVideo ? <CircularProgress size={18} sx={{ ml: 2 }} /> : null}
                </Toolbar>
            </AppBar>

            {/* ── Main body: two-column layout ── */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* ── Left panel: System Glossaries ── */}
                <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', overflow: 'hidden' }}>
                    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>System Glossaries</Typography>
                        {loadingList ? (
                            <CircularProgress size={14} />
                        ) : (
                            <Chip label={systemGlossaries.length} size="small" variant="outlined" />
                        )}
                        <Button
                            size="small"
                            disabled={loadingList}
                            onClick={() => {
                                setLoadingList(true);
                                glossaryServiceInstance
                                    .listGlossaries()
                                    .then((data) => setSystemGlossaries(normalizeGlossaryList(data)))
                                    .catch((err) => console.error('Failed to refresh system glossaries', err))
                                    .finally(() => setLoadingList(false));
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Glossary list */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {!loadingList && systemGlossaries.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No glossaries found in the system.
                            </Typography>
                        ) : (
                            <List dense disablePadding>
                                {systemGlossaries.map((g) => {
                                    const key = glossaryKey(g.filename, g.subfolder);
                                    const isSelected = selectedGlossaryKey === key;
                                    return (
                                        <ListItemButton
                                            key={key}
                                            selected={isSelected}
                                            onClick={() => handleSelectSystemGlossary(g)}
                                        >
                                            <ListItemText
                                                primary={g.filename}
                                                secondary={g.subfolder || 'root'}
                                            />
                                            {loadingTerms && isSelected ? <CircularProgress size={14} /> : null}
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        )}
                    </Box>

                    {/* Pending Review summary — visible to glossary managers / admins only */}
                    {canManage && selectedGlossaryKey ? (
                        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.5, py: 1, backgroundColor: '#fff8e1' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
                                    Pending Review
                                </Typography>
                                {loadingPending ? <CircularProgress size={12} /> : (
                                    <Chip
                                        label={Object.keys(pendingTerms).length}
                                        size="small"
                                        color={Object.keys(pendingTerms).length > 0 ? 'warning' : 'default'}
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                            {Object.keys(pendingTerms).length > 0 ? (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    fullWidth
                                    sx={{ mt: 0.75 }}
                                    onClick={() => setShowPendingReview(true)}
                                >
                                    Review Changes →
                                </Button>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                    No pending changes.
                                </Typography>
                            )}
                        </Box>
                    ) : null}

                    {/* Glossary history — tracked in backend MongoDB */}
                    {selectedGlossaryKey && !loadingTerms ? (
                        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5, backgroundColor: '#fafafa' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                Glossary History
                            </Typography>
                            {metadata ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                    <Typography variant="caption">Created by: <strong>{metadata.createdBy || '—'}</strong></Typography>
                                    <Typography variant="caption">Created: <strong>{metadata.createdDate ? new Date(metadata.createdDate).toLocaleString() : '—'}</strong></Typography>
                                    <Typography variant="caption">Updated by: <strong>{metadata.updatedBy || '—'}</strong></Typography>
                                    <Typography variant="caption">Updated: <strong>{metadata.lastModifiedDate ? new Date(metadata.lastModifiedDate).toLocaleString() : '—'}</strong></Typography>
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No history yet — will be tracked after first save.
                                </Typography>
                            )}
                        </Box>
                    ) : null}
                </Box>

                {/* ── Right panel: Generate + Terms  OR  Pending Review ── */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* ════ PENDING REVIEW MODE ════ */}
                {showPendingReview ? (
                    <>
                        {/* Toolbar */}
                        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#fff8e1' }}>
                            <Button size="small" variant="outlined" onClick={() => setShowPendingReview(false)}>← Back to Terms</Button>
                            <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                Pending Review — {Object.keys(pendingTerms).length} word(s),&nbsp;
                                {Object.values(pendingTerms).reduce((s, arr) => s + arr.length, 0)} proposal(s)
                            </Typography>
                            {approving ? <CircularProgress size={16} /> : null}
                            <Tooltip title="Approve the latest proposal for every source word">
                                <span>
                                    <Button size="small" variant="contained" color="success"
                                        disabled={approving || Object.keys(pendingTerms).length === 0}
                                        onClick={() => {
                                            // Pick the last (most recent) proposal per source word
                                            const approvedMap = {};
                                            Object.entries(pendingTerms).forEach(([src, proposals]) => {
                                                approvedMap[src] = proposals.length - 1;
                                            });
                                            handleApproveTerms(approvedMap, []);
                                        }}>
                                        Approve All
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Reject all pending changes">
                                <span>
                                    <Button size="small" variant="outlined" color="error"
                                        disabled={approving || Object.keys(pendingTerms).length === 0}
                                        onClick={() => handleApproveTerms({}, Object.keys(pendingTerms))}>
                                        Reject All
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>

                        {/* Alerts */}
                        {(error || successMessage) ? (
                            <Box sx={{ p: 1, pb: 0.5 }}>
                                {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
                                {successMessage ? <Alert severity="success" onClose={() => setSuccessMessage('')}>{successMessage}</Alert> : null}
                            </Box>
                        ) : null}

                        {/* Pending table — one row per proposal; multiple proposals per source word are shown consecutively */}
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {Object.keys(pendingTerms).length === 0 && !loadingPending ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography color="text.secondary">No pending changes to review.</Typography>
                                    <Button sx={{ mt: 2 }} variant="outlined" onClick={() => setShowPendingReview(false)}>← Back to Terms</Button>
                                </Box>
                            ) : null}
                            {loadingPending ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                            ) : null}
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 90 }}>Change</TableCell>
                                        <TableCell>Source Term</TableCell>
                                        <TableCell>Current Value</TableCell>
                                        <TableCell>Proposed Value</TableCell>
                                        <TableCell>Submitted By</TableCell>
                                        <TableCell sx={{ width: 200 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(pendingTerms).flatMap(([source, proposals]) => {
                                        const hasMultiple = proposals.length > 1;
                                        return proposals.map((pt, idx) => {
                                            const opBg = pt.operation === 'add' ? '#f1f8e9' : pt.operation === 'delete' ? '#ffebee' : '#fff3e0';
                                            const opColor = pt.operation === 'add' ? '#2e7d32' : pt.operation === 'delete' ? '#c62828' : '#e65100';
                                            const opLabel = pt.operation === 'add' ? 'Add' : pt.operation === 'delete' ? 'Delete' : 'Edit';
                                            return (
                                                <TableRow key={`${source}-${idx}`} sx={{ backgroundColor: opBg }}>
                                                    <TableCell>
                                                        <Chip label={opLabel} size="small" sx={{ backgroundColor: opColor, color: '#fff', fontWeight: 600 }} />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        {idx === 0 ? source : (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Typography variant="body2" sx={{ color: 'text.disabled' }}>↳</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{source}</Typography>
                                                                <Chip label={`option ${idx + 1}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'text.secondary', fontStyle: pt.previousTarget ? 'normal' : 'italic' }}>
                                                        {pt.previousTarget || '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: pt.operation !== 'delete' ? 600 : 'normal', color: pt.operation === 'delete' ? 'error.main' : 'inherit', fontStyle: pt.operation === 'delete' ? 'italic' : 'normal' }}>
                                                        {pt.operation === 'delete' ? '(remove)' : pt.target}
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{pt.submittedBy}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Tooltip title={hasMultiple ? 'Approve this proposal; all other proposals for this word are discarded' : 'Approve'}>
                                                                <span>
                                                                    <Button size="small" variant="contained" color="success"
                                                                        disabled={approving}
                                                                        onClick={() => handleApproveTerms({ [source]: idx }, [])}>
                                                                        Approve
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Reject all proposals for this word">
                                                                <span>
                                                                    <Button size="small" variant="outlined" color="error"
                                                                        disabled={approving}
                                                                        onClick={() => handleApproveTerms({}, [source])}>
                                                                        Reject
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        });
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                ) : (<>

                    {/* Alerts */}
                    {(error || successMessage) ? (
                        <Box sx={{ p: 1, pb: 0.5 }}>
                            {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
                            {successMessage ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Alert severity="success" sx={{ flex: 1 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
                                    {savedTermCount !== null ? <Chip label={`${savedTermCount} terms total`} color="success" size="small" /> : null}
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}

                    {/* Generate section — video, text file, or manual language pair */}
                    {isTextFile ? (
                        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Generate from This Text File</Typography>
                            {stageError ? <Alert severity="warning" sx={{ mb: 0.75 }}>{stageError}</Alert> : null}
                            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr auto' }, alignItems: 'center' }}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Source Language</InputLabel>
                                    <Select value={sourceLanguage} label="Source Language" onChange={(e) => { setSourceLanguage(e.target.value); setAutoSelectDone(false); }}>
                                        {Object.keys(textFileData?.translations || {}).map((lang) => (
                                            <MenuItem key={`source-${lang}`} value={lang}>{lang}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Target Language</InputLabel>
                                    <Select value={targetLanguage} label="Target Language" onChange={(e) => { setTargetLanguage(e.target.value); setAutoSelectDone(false); }}>
                                        {Object.keys(textFileData?.translations || {})
                                            .filter((lang) => lang !== sourceLanguage)
                                            .map((lang) => (
                                                <MenuItem key={`target-${lang}`} value={lang}>{lang}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={subfolderOptions}
                                    inputValue={subfolder}
                                    onInputChange={(_, newValue) => setSubfolder(newValue === '(root — default)' ? '' : newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Subfolder" size="small" placeholder="e.g. vachanamrutiji" />
                                    )}
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleGenerate}
                                    disabled={generating || !sourceLanguage || !targetLanguage}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {generating ? 'Generating…' : 'Generate'}
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Glossary file: <strong>{filename || 'N/A'}</strong>
                                {loadingTerms ? <CircularProgress size={12} sx={{ ml: 1 }} /> : null}
                            </Typography>
                        </Box>
                    ) : videoId ? (
                        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Generate from This Video</Typography>
                            {stageError ? <Alert severity="warning" sx={{ mb: 0.75 }}>{stageError}</Alert> : null}
                            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr auto' }, alignItems: 'center' }}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Source Language</InputLabel>
                                    <Select value={sourceLanguage} label="Source Language" onChange={(e) => { setSourceLanguage(e.target.value); setAutoSelectDone(false); }}>
                                        {(videoData?.videoLanguages || []).map((item) => (
                                            <MenuItem key={`source-${item.languageName}`} value={item.languageName}>{item.languageName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Target Language</InputLabel>
                                    <Select value={targetLanguage} label="Target Language" onChange={(e) => { setTargetLanguage(e.target.value); setAutoSelectDone(false); }}>
                                        {(videoData?.videoLanguages || [])
                                            .filter((item) => item.languageName !== sourceLanguage)
                                            .map((item) => (
                                                <MenuItem key={`target-${item.languageName}`} value={item.languageName}>{item.languageName}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={subfolderOptions}
                                    inputValue={subfolder}
                                    onInputChange={(_, newValue) => setSubfolder(newValue === '(root — default)' ? '' : newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Subfolder" size="small" placeholder="e.g. vachanamrutiji" />
                                    )}
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleGenerate}
                                    disabled={generating || Boolean(stageError) || !sourceLanguage || !targetLanguage}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {generating ? 'Generating…' : 'Generate'}
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Glossary file: <strong>{filename || 'N/A'}</strong>
                                {loadingTerms ? <CircularProgress size={12} sx={{ ml: 1 }} /> : null}
                            </Typography>
                        </Box>
                    ) : (
                        /* No videoId — language pickers for manual file selection */
                        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Language Pair</Typography>
                            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
                                <TextField size="small" label="Source Language" value={sourceLanguage} onChange={(e) => { setSourceLanguage(e.target.value); setAutoSelectDone(false); }} placeholder="e.g. Gujarati" fullWidth />
                                <TextField size="small" label="Target Language" value={targetLanguage} onChange={(e) => { setTargetLanguage(e.target.value); setAutoSelectDone(false); }} placeholder="e.g. English" fullWidth />
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={subfolderOptions}
                                    inputValue={subfolder}
                                    onInputChange={(_, newValue) => setSubfolder(newValue === '(root — default)' ? '' : newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Subfolder" size="small" placeholder="e.g. vachanamrutiji" />
                                    )}
                                    fullWidth
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Glossary file: <strong>{filename || 'N/A'}</strong>
                                {loadingTerms ? <CircularProgress size={12} sx={{ ml: 1 }} /> : null}
                            </Typography>
                        </Box>
                    )}

                    {/* Terms toolbar: search + chips + toggle */}
                    <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
                        <TextField
                            size="small"
                            label="Search terms"
                            placeholder="Type to search source or target…"
                            value={termFilter}
                            onChange={(e) => setTermFilter(e.target.value)}
                            sx={{ flex: 1, minWidth: 200 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: termFilter ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setTermFilter('')}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        {existingTermCount > 0 ? (
                            <Chip label={`${existingTermCount} in glossary`} size="small" sx={{ backgroundColor: '#e3f2fd', color: '#0d47a1', fontWeight: 500 }} />
                        ) : null}
                        {generatedTermCount > 0 ? (
                            <Chip label={`${generatedTermCount} generated`} size="small" sx={{ backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 500 }} />
                        ) : null}
                        {pendingRows.length > 0 ? (
                            <Chip label={`${pendingRows.length} new/changed`} size="small" color="warning" variant="outlined" />
                        ) : null}
                        <Button
                            size="small"
                            variant={showAllTerms ? 'contained' : 'outlined'}
                            onClick={() => setShowAllTerms((v) => !v)}
                        >
                            {showAllTerms ? 'Show new/changed only' : 'Show all terms'}
                        </Button>
                        {displayedRows.length < (showAllTerms ? rows : pendingRows).length ? (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                Showing {displayedRows.length} of {showAllTerms ? rows.length : pendingRows.length}
                            </Typography>
                        ) : null}
                    </Box>

                    {/* Color legend */}
                    {rows.length > 0 ? (
                        <Box sx={{ px: 2, py: 0.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider', backgroundColor: '#fafafa' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Legend:</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: '#f1f8e9', border: '1px solid #c5e1a5' }} />
                                <Typography variant="caption">New — not yet in glossary</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: '#fff3e0', border: '1px solid #ffcc80' }} />
                                <Typography variant="caption">Conflict — generated differs from saved</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }} />
                                <Typography variant="caption">Already saved (no change)</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, backgroundColor: '#ffebee', border: '1px solid #ef9a9a' }} />
                                <Typography variant="caption">Marked for delete</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                For existing terms: click Unlock, then Delete term.
                            </Typography>
                        </Box>
                    ) : null}

                    {/* Terms table — scrollable */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="none" sx={{ pl: 1, whiteSpace: 'nowrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Checkbox
                                                size="small"
                                                checked={rows.length > 0 && rows.every((r) => Boolean(selectedRows[r.source]))}
                                                indeterminate={
                                                    rows.some((r) => Boolean(selectedRows[r.source])) &&
                                                    !rows.every((r) => Boolean(selectedRows[r.source]))
                                                }
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setSelectedRows((prev) => {
                                                        const next = { ...prev };
                                                        rows.forEach((r) => {
                                                            next[r.source] = r.existingTarget ? true : checked;
                                                        });
                                                        return next;
                                                    });
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" title="Check rows to include them when saving">Save?</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Existing Target</TableCell>
                                    <TableCell>Generated Target</TableCell>
                                    <TableCell>Final Target</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedRows.map((row) => {
                                    const isExisting = row.status === 'existing-only';
                                    const isUnlocked = editUnlocked.has(row.source);
                                    const isMarkedDeleted = deletedSources.has(row.source);
                                    const rowBg = row.status === 'delete' ? '#ffebee' : row.status === 'conflict' ? '#fff3e0' : row.status === 'new' ? '#f1f8e9' : undefined;
                                    return (
                                        <TableRow key={row.source} sx={rowBg ? { backgroundColor: rowBg } : undefined}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={Boolean(row.existingTarget) || Boolean(selectedRows[row.source])}
                                                    disabled={isExisting && !isUnlocked}
                                                    onChange={(e) => {
                                                        if (row.existingTarget) return;
                                                        const checked = e.target.checked;
                                                        setSelectedRows((prev) => ({ ...prev, [row.source]: checked }));
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{row.source}</TableCell>
                                            <TableCell>{row.existingTarget || '-'}</TableCell>
                                            <TableCell>{row.generatedTarget || '-'}</TableCell>
                                            <TableCell>
                                                {isExisting && !isUnlocked ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{row.finalTarget}</Typography>
                                                        <IconButton size="small" title="Unlock to edit or delete this existing term" onClick={() => setEditUnlocked((prev) => { const s = new Set(prev); s.add(row.source); return s; })}>
                                                            <LockOpenIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <TextField
                                                            value={row.finalTarget || ''}
                                                            onChange={(e) => {
                                                                setDeletedSources((prev) => {
                                                                    const next = new Set(prev);
                                                                    next.delete(row.source);
                                                                    return next;
                                                                });
                                                                setEditedTargets((prev) => ({ ...prev, [row.source]: e.target.value }));
                                                            }}
                                                            size="small"
                                                            fullWidth
                                                        />
                                                        {row.existingTarget ? (
                                                            <Button
                                                                size="small"
                                                                variant={isMarkedDeleted ? 'contained' : 'outlined'}
                                                                color="error"
                                                                onClick={() => {
                                                                    setDeletedSources((prev) => {
                                                                        const next = new Set(prev);
                                                                        if (next.has(row.source)) {
                                                                            next.delete(row.source);
                                                                        } else {
                                                                            next.add(row.source);
                                                                        }
                                                                        return next;
                                                                    });
                                                                }}
                                                                sx={{ whiteSpace: 'nowrap' }}
                                                            >
                                                                {isMarkedDeleted ? 'Undo Delete' : 'Delete term'}
                                                            </Button>
                                                        ) : null}
                                                        {isUnlocked ? (
                                                            <IconButton size="small" title="Re-lock this term" onClick={() => {
                                                                setEditUnlocked((prev) => { const s = new Set(prev); s.delete(row.source); return s; });
                                                                setEditedTargets((prev) => { const next = { ...prev }; delete next[row.source]; return next; });
                                                            }}>
                                                                <LockIcon fontSize="small" />
                                                            </IconButton>
                                                        ) : null}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {displayedRows.length === 0 && !loadingTerms ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                Select a glossary from the list, or generate terms from this {isTextFile ? 'text file' : 'video'}.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                                {loadingTerms ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <CircularProgress size={20} />
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* ── Footer actions bar with back button ── */}
                    <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        {backTo && (
                            <Button
                                size="small"
                                onClick={() => navigate(backTo)}
                                sx={{ whiteSpace: 'nowrap' }}
                                variant="outlined"
                            >
                                ← Back
                            </Button>
                        )}
                        {saveableCount > 0 ? (
                            <Chip
                                label={`${saveableCount} changes ready to save`}
                                size="small"
                                color="warning"
                                title="Includes new terms, updates, and deletions"
                            />
                        ) : null}
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Changes will be queued for a glossary manager to review and approve before going live.">
                            <span>
                                <Button variant="contained" color="warning" onClick={handleSubmitPending} disabled={saving || saveableCount === 0}>
                                    {saving ? 'Submitting…' : 'Submit for Review'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </>)}
                </Box>
            </Box>
        </Box>
    );
}
